/**
 * The navigation grid as the soldiers see it (06 §8): a dithered tint on the ground, one square
 * per 1 m cell - red where a man cannot go, amber where he would rather not (trenches, mined
 * ground) - and only for the cells round the camera. Dither-thinned like smoke, so the ground
 * shows through and nothing is blended. Rebuilt only when the view moves a cell or the grid changes.
 */

import { type Material, Mesh, type Scene, type Texture, Vector3 } from 'three';
import type { Statics } from '../../sim/statics';
import { MeshBuilder } from '../kit/MeshBuilder';
import { createDitherMaterial } from '../materials/dither';
import { createGradientMap } from '../materials/toon';
import type { Palette } from '../palette';
import type { GroundY } from './layers';

/** Half-size of the window of cells drawn, in cells: a little more than the 36 x 28 m view. */
const HALF_X = 22;
const HALF_Z = 18;
const COVERAGE = 0.3125;

export class NavLayer {
  private readonly gradient: Texture;
  private readonly material: Material;
  private mesh: Mesh | null = null;
  private key = '';

  constructor(private readonly scene: Scene, private readonly palette: Palette, private readonly groundY: GroundY) {
    this.gradient = createGradientMap();
    const coverage = { value: COVERAGE };
    this.material = createDitherMaterial({ gradientMap: this.gradient, coverage, emissiveBoost: 0.6 });
    Object.assign(this.material, { polygonOffset: true, polygonOffsetFactor: -1, polygonOffsetUnits: -4 });
  }

  /** Cells in the current mesh's window that were tinted (tests, overlay readout). */
  tinted = 0;

  update(statics: Statics, fx: number, fz: number): void {
    const cx = Math.floor(fx);
    const cz = Math.floor(fz);
    const key = `${cx},${cz},${statics.version}`;
    if (key === this.key) return;
    this.key = key;
    this.clear();

    const { width, height, blocked, cost } = statics.nav;
    const mb = new MeshBuilder();
    const no = this.palette.color('enemyAccent.0');
    const dear = this.palette.color('playerAccent.0');
    const v = (x: number, y: number, z: number): Vector3 => new Vector3(x, y, z);
    this.tinted = 0;
    for (let z = Math.max(0, cz - HALF_Z); z <= Math.min(height - 1, cz + HALF_Z); z++) {
      for (let x = Math.max(0, cx - HALF_X); x <= Math.min(width - 1, cx + HALF_X); x++) {
        const i = z * width + x;
        const stop = blocked[i] === 1;
        if (!stop && (cost?.[i] ?? 1) <= 1) continue;
        const y = this.groundY(x + 0.5, z + 0.5) + 0.07;
        // A gap between squares keeps the grid legible where a whole compound is blocked.
        const [x0, x1, z0, z1] = [x + 0.06, x + 0.94, z + 0.06, z + 0.94];
        mb.quad(v(x0, y, z1), v(x1, y, z1), v(x1, y, z0), v(x0, y, z0), stop ? no : dear);
        this.tinted++;
      }
    }
    if (mb.triangleCount === 0) return;
    this.mesh = new Mesh(mb.build(), this.material);
    this.mesh.name = 'debug-nav';
    this.mesh.renderOrder = 9;
    this.scene.add(this.mesh);
  }

  private clear(): void {
    if (!this.mesh) return;
    this.scene.remove(this.mesh);
    this.mesh.geometry.dispose();
    this.mesh = null;
  }

  dispose(): void {
    this.clear();
    this.key = '';
    this.material.dispose();
    this.gradient.dispose();
  }
}
